<template>
    <el-container class="deliver-page">
        <el-main class="data-container">
            <section class="query-panel">
                <div class="query-title">
                    <span class="title-mark"></span>
                    <div>
                        <h3>Deliver / 功能模式号查询</h3>
                        <p>{{ querySubtitle }}</p>
                    </div>
                </div>

                <div class="query-controls">
                    <el-select v-model="serverName" class="server-select" :disabled="loading">
                        <el-option label="CN Server" value="CN Server" />
                        <el-option label="US Server" value="US Server" />
                    </el-select>

                    <el-radio-group v-model="queryType" class="query-type" :disabled="loading">
                        <el-radio-button label="deliver">Deliver</el-radio-button>
                        <el-radio-button label="mode">功能模式号</el-radio-button>
                    </el-radio-group>

                    <el-input
                        v-if="queryType === 'deliver'"
                        v-model.trim="deliverCode"
                        placeholder="请输入 Deliver，例如 6437"
                        maxlength="5"
                        clearable
                        :disabled="loading"
                        class="main-input"
                        @keyup.enter="handleSearch"
                    />
                    <el-input
                        v-else
                        v-model.trim="modeId"
                        placeholder="请输入功能模式号，例如 0x001001"
                        maxlength="8"
                        clearable
                        :disabled="loading"
                        class="main-input"
                        @keyup.enter="handleSearch"
                    />

                    <el-select v-model="selectedYear" class="year-select" :disabled="loading">
                        <el-option
                            v-for="item in yearOptions"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        />
                    </el-select>

                    <el-button type="primary" :icon="Search" :loading="loading" :disabled="loading" @click="handleSearch">
                        {{ queryType === 'deliver' ? '按 Deliver 查询' : '按功能模式号查询' }}
                    </el-button>
                </div>

                <div class="rule-note">
                    <el-icon><InfoFilled /></el-icon>
                    <span>{{ ruleNoteText }}</span>
                </div>
            </section>

            <section v-if="queried" class="summary-grid">
                <template v-if="queryType === 'deliver'">
                    <div class="summary-card">
                        <span>Deliver</span>
                        <strong>{{ summary.deliver || '-' }}</strong>
                    </div>
                    <div class="summary-card">
                        <span>车型名称</span>
                        <strong>{{ summary.carname || '未配置' }}</strong>
                    </div>
                    <div class="summary-card">
                        <span>年款规则</span>
                        <strong>{{ yearRuleText }}</strong>
                    </div>
                    <div class="summary-card">
                        <span>返回 VIN</span>
                        <strong>{{ tableData.length }}</strong>
                    </div>
                    <div class="summary-card">
                        <span>数据来源</span>
                        <strong>
                            <el-tag :type="querySourceTagType" effect="plain">{{ querySourceText }}</el-tag>
                        </strong>
                    </div>
                </template>
                <template v-else>
                    <div class="summary-card">
                        <span>功能模式号</span>
                        <strong>{{ modeSummary.modeId || '-' }}</strong>
                    </div>
                    <div class="summary-card">
                        <span>功能名</span>
                        <strong>{{ modeSummary.funName || '-' }}</strong>
                    </div>
                    <div class="summary-card">
                        <span>功能分类</span>
                        <strong>{{ resetTypeText }}</strong>
                    </div>
                    <div class="summary-card">
                        <span>命中 VIN</span>
                        <strong>{{ tableData.length }}</strong>
                    </div>
                    <div class="summary-card">
                        <span>数据来源</span>
                        <strong>
                            <el-tag :type="querySourceTagType" effect="plain">{{ querySourceText }}</el-tag>
                        </strong>
                    </div>
                </template>
            </section>

            <section v-if="queryType === 'mode' && queried" class="mode-summary">
                <el-descriptions :column="3" border size="small">
                    <el-descriptions-item label="diagVersion">{{ modeSummary.diagVersion || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="开发状态">
                        <el-tag :type="modeSummary.display ? 'success' : 'info'" size="small">
                            {{ modeSummary.display ? '已开发' : '未开发' }}
                        </el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="downloadName">{{ modeSummary.downloadName || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="app">{{ modeSummary.procUid || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="mdx">
                        <div v-if="modeSummary.mdx.length" class="mdx-tags">
                            <el-tag v-for="mdx in modeSummary.mdx" :key="mdx" size="small" effect="plain">
                                {{ mdx }}
                            </el-tag>
                        </div>
                        <span v-else>-</span>
                    </el-descriptions-item>
                    <el-descriptions-item label="查询来源">
                        <el-tag :type="querySourceTagType" size="small" effect="plain">{{ querySourceText }}</el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="selectedDeliver">
                        <div class="selected-deliver">
                            <span>{{ modeSummary.selectedDeliver || '-' }}</span>
                            <el-button
                                v-if="canChangeModeDeliver"
                                size="small"
                                text
                                type="primary"
                                :disabled="loading"
                                @click="handleChangeModeDeliver"
                            >
                                更换 Deliver
                            </el-button>
                        </div>
                    </el-descriptions-item>
                    <el-descriptions-item label="deliverList" :span="3">{{ deliverListText }}</el-descriptions-item>
                </el-descriptions>
            </section>

            <section
                v-if="queryType === 'mode' && needUserSelection && selectionQuestions.length"
                class="selection-panel"
            >
                <div class="selection-header">
                    <el-icon><WarningFilled /></el-icon>
                    <span>{{ selectionPanelText }}</span>
                </div>
                <div class="selection-controls">
                    <template v-for="question in selectionQuestions" :key="question.key">
                        <div v-if="isInfoQuestion(question)" class="condition-info-group">
                            <div class="condition-info-title">{{ question.label }}</div>
                            <div v-if="question.options && question.options.length" class="condition-tags">
                                <el-tag
                                    v-for="option in question.options"
                                    :key="option.value || option.label"
                                    type="warning"
                                    effect="plain"
                                >
                                    {{ conditionOptionText(option) }}
                                </el-tag>
                            </div>
                            <span v-else class="muted-text">暂无可展示条件</span>
                        </div>
                        <el-form-item
                            v-else
                            :label="question.label"
                        >
                            <el-select
                                v-model="selectionValues[question.key]"
                                clearable
                                filterable
                                :disabled="loading"
                                placeholder="请选择"
                                @change="handleSelectionQuestionChange(question, $event)"
                            >
                                <el-option
                                    v-for="option in question.options || []"
                                    :key="option.value"
                                    :label="option.label"
                                    :value="option.value"
                                />
                            </el-select>
                        </el-form-item>
                    </template>
                </div>
            </section>

            <section
                v-if="queryType === 'mode' && hasFilterRequirements"
                class="requirements-panel"
            >
                <div class="requirements-header">
                    <div>
                        <h3>需要达成的筛选条件</h3>
                        <p>当前指定 Deliver 暂无命中 VIN，可按这些条件检查特征码和模块零件号</p>
                    </div>
                    <el-tag type="warning" effect="plain">未命中说明</el-tag>
                </div>

                <div class="requirements-content">
                    <div v-if="featureRequirementOptions.length" class="requirement-group">
                        <div class="requirement-title">特征码条件</div>
                        <div class="condition-tags">
                            <el-tag
                                v-for="option in featureRequirementOptions"
                                :key="option.value || option.code || option.label"
                                type="warning"
                                effect="plain"
                            >
                                {{ conditionOptionText(option) }}
                            </el-tag>
                        </div>
                    </div>

                    <div v-if="mdxAssemblyRequirements.length" class="requirement-group">
                        <div class="requirement-title">MDX 对应零件号</div>
                        <el-table
                            :data="mdxAssemblyRequirements"
                            border
                            size="small"
                            class="requirements-table"
                        >
                            <el-table-column prop="moduleAcronym" label="模块" width="96" align="center" />
                            <el-table-column prop="moduleAddress" label="地址" width="104" align="center" />
                            <el-table-column prop="mdxUid" label="MDX UID" min-width="130" align="center" />
                            <el-table-column prop="procUid" label="procUid" min-width="130" align="center" />
                            <el-table-column label="assemblys" min-width="260">
                                <template #default="{ row }">
                                    <div class="assembly-tags" v-if="row.assemblys && row.assemblys.length">
                                        <el-tag
                                            v-for="assembly in row.assemblys"
                                            :key="assembly"
                                            size="small"
                                            type="info"
                                            effect="plain"
                                        >
                                            {{ assembly }}
                                        </el-tag>
                                    </div>
                                    <span v-else class="muted-text">无零件号限制</span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </div>
            </section>

            <section v-if="queryType === 'mode' && deepScanSummary" class="deep-progress-panel">
                <div class="deep-progress-header">
                    <span>深度查询进度</span>
                    <strong>{{ deepScanSummary }}</strong>
                </div>
                <el-progress
                    :percentage="deepScanPercent"
                    :stroke-width="10"
                    :show-text="false"
                />
            </section>

            <section class="table-section">
                <div v-if="loading" class="table-loading-overlay">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">{{ loadingText }}</div>
                </div>
                <div class="section-header">
                    <div>
                        <h3>VIN 结果</h3>
                        <p>{{ tableHintText }}</p>
                    </div>
                    <div class="section-actions">
                        <el-button
                            v-if="canDeepQuery"
                            type="warning"
                            plain
                            :icon="Search"
                            :loading="loading"
                            :disabled="loading"
                            @click="handleDeepQueryModeVin"
                        >
                            深度查询
                        </el-button>
                        <el-button :icon="Refresh" plain :loading="loading" :disabled="loading" @click="handleSearch">
                            刷新
                        </el-button>
                    </div>
                </div>

                <el-table
                    v-if="tableData.length"
                    :data="tableData"
                    border
                    stripe
                    class="custom-table"
                >
                    <el-table-column prop="vin" label="VIN" min-width="180" align="center" />
                    <el-table-column prop="year" label="年款" width="90" align="center" />
                    <el-table-column prop="deliver" label="Deliver" width="110" align="center" />
                    <el-table-column prop="carname" label="车型名称" min-width="150" align="center">
                        <template #default="{ row }">
                            {{ row.carname || '未配置' }}
                        </template>
                    </el-table-column>
                    <el-table-column prop="dateStr" label="最近采集时间" min-width="168" align="center" />
                    <el-table-column label="协议" min-width="140" align="center">
                        <template #default="{ row }">
                            <div class="protocol-tags" v-if="row.protocols && row.protocols.length">
                                <el-tag v-for="protocol in row.protocols" :key="protocol" size="small">
                                    {{ protocol }}
                                </el-tag>
                            </div>
                            <span v-else class="muted-text">无</span>
                        </template>
                    </el-table-column>
                    <el-table-column
                        v-if="queryType === 'mode'"
                        prop="matchStatus"
                        label="匹配状态"
                        width="124"
                        align="center"
                    >
                        <template #default="{ row }">
                            <el-tag :type="matchStatusType(row.matchStatus)" size="small">
                                {{ matchStatusText(row.matchStatus) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column
                        v-if="queryType === 'mode'"
                        prop="matchedModule"
                        label="匹配模块"
                        width="110"
                        align="center"
                    />
                    <el-table-column
                        v-if="queryType === 'mode'"
                        prop="matchedAddress"
                        label="ECU 地址"
                        width="110"
                        align="center"
                    />
                    <el-table-column
                        v-if="queryType === 'mode'"
                        prop="matchedPartNumber"
                        label="零件号"
                        min-width="160"
                        align="center"
                    >
                        <template #default="{ row }">
                            {{ row.matchedPartNumber || '-' }}
                        </template>
                    </el-table-column>
                    <el-table-column
                        v-if="queryType === 'mode'"
                        prop="mdx"
                        label="mdx"
                        min-width="130"
                        align="center"
                    >
                        <template #default="{ row }">
                            {{ row.mdx || '-' }}
                        </template>
                    </el-table-column>
                    <el-table-column
                        v-if="queryType === 'mode'"
                        prop="matchReason"
                        label="匹配原因"
                        min-width="260"
                        show-overflow-tooltip
                    />
                    <el-table-column label="操作" width="96" fixed="right" align="center">
                        <template #default="{ row }">
                            <div class="action-buttons">
                                <el-tooltip content="下载命令文本" placement="top">
                                    <el-button
                                        size="small"
                                        type="primary"
                                        plain
                                        :icon="Document"
                                        circle
                                        :disabled="loading"
                                        @click="handleDownloadCommand(row)"
                                    />
                                </el-tooltip>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>

                <el-empty
                    v-else
                    :description="emptyDescription"
                    :image-size="90"
                />
            </section>
        </el-main>
    </el-container>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Document, InfoFilled, Refresh, Search, WarningFilled } from '@element-plus/icons-vue';
import { DownloadSomeOne, getSomeOne } from '@/api/users';

const serverName = ref('CN Server');
const queryType = ref('deliver');
const deliverCode = ref('');
const modeId = ref('');
const selectedYear = ref('all');
const loading = ref(false);
const queried = ref(false);
const lastModeId = ref('');
const tableData = ref([]);
const needUserSelection = ref(false);
const selectionQuestions = ref([]);
const filterRequirements = ref(null);
const selectionValues = reactive({});
const modeQuerySuccess = ref(false);
const modeResultLimit = 10;
const deepScanLimit = '50000';
const deepBatchScan = '5000';
const deepScanSummary = ref('');
const deepScanProgress = ref({
    scanned: 0,
    scanLimit: Number(deepScanLimit),
    matched: 0,
});
const queryMeta = ref({
    source: '',
    hasMore: false,
    vinDeliverIndexHits: 0,
    totalMs: null,
});
const modeNoResultTip = '目前未找到命中 VIN，可点击深度查询扩大扫描；仍无结果请主动切换服务器查询：国内的切海外，海外的切国内。';

const summary = ref({
    deliver: '',
    carname: '',
    year: '',
    scanned: 0,
});

const modeSummary = ref({
    modeId: '',
    funName: '',
    diagVersion: '',
    display: false,
    downloadName: '',
    procUid: '',
    mdx: [],
    resetType: [],
    deliverList: [],
    selectedDeliver: '',
});

const yearOptions = computed(() => {
    const currentYear = new Date().getFullYear();
    const maxYear = Math.min(currentYear + 1, 2027);
    const minYear = Math.max(currentYear - 4, 1998);
    const options = [{ label: '全部年款', value: 'all' }];

    for (let year = maxYear; year >= minYear; year -= 1) {
        options.push({ label: `${year} 年款`, value: String(year) });
    }

    return options;
});

const yearRuleText = computed(() => {
    if (summary.value.year === 'all') {
        return '全部年款';
    }

    return `${summary.value.year || selectedYear.value} 年款`;
});

const resetTypeText = computed(() => formatArray(modeSummary.value.resetType) || '-');
const deliverListText = computed(() => formatArray(modeSummary.value.deliverList) || '-');
const canChangeModeDeliver = computed(() => {
    return queryType.value === 'mode' &&
        modeSummary.value.selectedDeliver &&
        Array.isArray(modeSummary.value.deliverList) &&
        modeSummary.value.deliverList.length > 1;
});

const canDeepQuery = computed(() => {
    return queryType.value === 'mode' &&
        queried.value &&
        modeQuerySuccess.value &&
        !needUserSelection.value &&
        tableData.value.length === 0 &&
        (queryMeta.value.source !== 'vdc_vin_deliver' || queryMeta.value.hasMore);
});

const querySourceText = computed(() => {
    if (queryMeta.value.source === 'vdc_vin_deliver') {
        return 'VIN 索引';
    }
    if (queryMeta.value.source === 'redis_scan') {
        return '兜底扫描';
    }
    return '-';
});

const querySourceTagType = computed(() => {
    if (queryMeta.value.source === 'vdc_vin_deliver') {
        return 'success';
    }
    if (queryMeta.value.source === 'redis_scan') {
        return 'warning';
    }
    return 'info';
});

const hasInfoSelectionQuestions = computed(() => selectionQuestions.value.some((question) => isInfoQuestion(question)));
const hasSelectableSelectionQuestions = computed(() => selectionQuestions.value.some((question) => !isInfoQuestion(question)));
const featureRequirementOptions = computed(() => {
    const requirements = filterRequirements.value || {};
    return Array.isArray(requirements.featureConditions) ? requirements.featureConditions : [];
});
const mdxAssemblyRequirements = computed(() => {
    const requirements = filterRequirements.value || {};
    return Array.isArray(requirements.mdxAssemblys) ? requirements.mdxAssemblys : [];
});
const hasFilterRequirements = computed(() => {
    return queried.value &&
        tableData.value.length === 0 &&
        Boolean(filterRequirements.value && filterRequirements.value.available) &&
        (featureRequirementOptions.value.length > 0 || mdxAssemblyRequirements.value.length > 0);
});

const querySubtitle = computed(() => {
    return queryType.value === 'mode'
        ? '按特殊功能模式号匹配候选 VIN，并根据 Deliver、XML 与零件号继续精确筛选'
        : '按 Deliver 从近期采集 VIN 中反查命中车辆';
});

const ruleNoteText = computed(() => {
    if (queryType.value === 'mode') {
        return '功能模式号查询先读取 app_online.cfg；命中 Deliver 后还必须通过 XML MDX/零件号筛选才计入结果，无法自动判断特征码时只展示命中所需条件。';
    }

    return 'Deliver 查询按 VIN 反查实际 deliver，并按年款规则筛选最近采集 VIN，适合快速查看某个 Deliver 覆盖的车辆样本。';
});

const selectionPanelText = computed(() => {
    const firstQuestion = selectionQuestions.value[0];
    if (firstQuestion && firstQuestion.key === 'deliver') {
        return '该功能模式适用多个 Deliver，选择后将自动查询';
    }

    if (hasInfoSelectionQuestions.value && !hasSelectableSelectionQuestions.value) {
        return '当前功能需要满足以下特征码条件才能命中名单';
    }

    return '该 Deliver 下存在无法自动筛选的车型配置条件，请选择后重新查询';
});

const tableHintText = computed(() => {
    if (queryType.value === 'mode') {
        if (deepScanSummary.value) {
            return deepScanSummary.value;
        }
        if (queryMeta.value.source === 'vdc_vin_deliver') {
            return '已优先使用 VIN Deliver 索引表筛选候选 VIN，再结合 XML app、MDX、assembly 和 VIN 零件号精确匹配';
        }

        return '按功能模式号命中 deliver 后，继续结合 XML app、MDX、assembly 和 VIN 零件号筛选，未通过精确匹配的 VIN 不占返回条数';
    }

    if (queryMeta.value.source === 'vdc_vin_deliver') {
        return '已优先使用 VIN Deliver 索引表返回命中 VIN';
    }

    return '按最近采集时间倒序扫描，命中 Deliver 后展示';
});

const deepScanPercent = computed(() => {
    const scanLimit = Number(deepScanProgress.value.scanLimit || deepScanLimit);
    if (!scanLimit) {
        return 0;
    }

    const percent = Math.floor((Number(deepScanProgress.value.scanned || 0) / scanLimit) * 100);
    return Math.min(100, Math.max(0, percent));
});

const loadingText = computed(() => {
    if (queryType.value === 'mode' && deepScanSummary.value) {
        return deepScanSummary.value;
    }

    return queryType.value === 'mode' ? '正在查询功能模式号' : '正在查询 Deliver';
});

const emptyDescription = computed(() => {
    if (!queried.value) {
        return queryType.value === 'mode' ? '请输入功能模式号后查询' : '请输入 Deliver 后查询';
    }

    return queryType.value === 'mode' ? modeNoResultTip : '暂无匹配 VIN';
});

function getUseBaseURL() {
    return serverName.value === 'CN Server';
}

function formatArray(value) {
    return Array.isArray(value) ? value.filter(Boolean).join(', ') : value;
}

function isInfoQuestion(question) {
    return Boolean(question && (question.inputType === 'info' || question.selectionRequired === false));
}

function conditionOptionText(option = {}) {
    if (option.label) {
        return option.label;
    }

    const code = option.code || option.value || '';
    const codeValue = option.codeValue ? `，value=${option.codeValue}` : '';
    return `${code}${codeValue}` || '-';
}

function modeSelectionNoticeText(stage) {
    if (stage === 'deliver') {
        return '请选择 Deliver，选择后将自动查询';
    }
    if (stage === 'feature_info') {
        return '当前功能存在特征码命中条件，已在筛选规则中列出';
    }
    if (stage === 'market') {
        return '该功能存在市场区域条件，请确认后重新查询';
    }

    return '存在无法自动筛选的车型配置，请选择后重新查询';
}

function validateDeliver() {
    if (!/^\d{4,5}$/.test(deliverCode.value)) {
        ElMessage.warning('Deliver 通常为 4-5 位数字');
        return false;
    }

    return true;
}

function validateModeId() {
    if (!/^(0x)?[0-9a-fA-F]{6}$/.test(modeId.value)) {
        ElMessage.warning('功能模式号格式应为 0x001001 或 001001');
        return false;
    }

    return true;
}

function resetModeSelection(data = {}) {
    needUserSelection.value = Boolean(data.needUserSelection);
    selectionQuestions.value = Array.isArray(data.selectionQuestions) ? data.selectionQuestions : [];

    const validKeys = new Set(
        selectionQuestions.value
            .filter((question) => !isInfoQuestion(question))
            .map((question) => question.key)
    );
    if (data.selectedDeliver) {
        validKeys.add('deliver');
        selectionValues.deliver = data.selectedDeliver;
    }

    Object.keys(selectionValues).forEach((key) => {
        if (!validKeys.has(key)) {
            delete selectionValues[key];
        }
    });
}

function updateFilterRequirements(data = {}) {
    const requirements = data.filterRequirements;
    filterRequirements.value = requirements && requirements.available ? requirements : null;
}

function resetFilterRequirements() {
    filterRequirements.value = null;
}

function resetQueryMeta() {
    queryMeta.value = {
        source: '',
        hasMore: false,
        vinDeliverIndexHits: 0,
        totalMs: null,
    };
}

function updateQueryMeta(data = {}) {
    const debugTiming = data.debugTiming || {};
    queryMeta.value = {
        source: data.source || '',
        hasMore: Boolean(data.hasMore),
        vinDeliverIndexHits: Number(debugTiming.vinDeliverIndexHits || 0),
        totalMs: Number.isFinite(Number(debugTiming.totalMs)) ? Number(debugTiming.totalMs) : null,
    };
}

function buildDeliverSelectionQuestion() {
    return [{
        key: 'deliver',
        label: '该功能模式适用多个 Deliver，请选择 Deliver',
        options: modeSummary.value.deliverList.map((deliver) => ({
            value: deliver,
            label: deliver,
        })),
    }];
}

function handleChangeModeDeliver() {
    if (!canChangeModeDeliver.value) {
        return;
    }

    resetDeepScanProgress();
    Object.keys(selectionValues).forEach((key) => {
        if (key !== 'deliver') {
            delete selectionValues[key];
        }
    });
    selectionValues.deliver = modeSummary.value.selectedDeliver;
    selectionQuestions.value = buildDeliverSelectionQuestion();
    needUserSelection.value = true;
    ElMessage.info('请选择新的 Deliver，选择后将自动查询');
}

async function handleSelectionQuestionChange(question, value) {
    if (question?.key !== 'deliver' || !value || loading.value) {
        return;
    }

    await handleQueryModeVin();
}

function updateModeSummary(data = {}) {
    modeSummary.value = {
        modeId: data.modeId || modeId.value,
        funName: data.funName || '',
        diagVersion: data.diagVersion || '',
        display: Boolean(data.display),
        downloadName: data.downloadName || '',
        procUid: data.procUid || '',
        mdx: Array.isArray(data.mdx) ? data.mdx : [],
        resetType: Array.isArray(data.resetType) ? data.resetType : [],
        deliverList: Array.isArray(data.deliverList) ? data.deliverList : [],
        selectedDeliver: data.selectedDeliver || '',
    };
}

function resetDeepScanProgress() {
    deepScanSummary.value = '';
    deepScanProgress.value = {
        scanned: 0,
        scanLimit: Number(deepScanLimit),
        matched: 0,
    };
}

function updateDeepScanProgress(scanned, scanLimit, matched) {
    const normalizedScanned = Number(scanned || 0);
    const normalizedScanLimit = Number(scanLimit || deepScanLimit);
    const normalizedMatched = Number(matched || 0);

    deepScanProgress.value = {
        scanned: normalizedScanned,
        scanLimit: normalizedScanLimit,
        matched: normalizedMatched,
    };
    deepScanSummary.value = `已扫描 ${normalizedScanned} / ${normalizedScanLimit} 条 VIN，已命中 ${normalizedMatched} 条`;
}

function updateDeepScanBatchProgress(scanned, scanLimit, matched, batchSize) {
    const normalizedScanned = Number(scanned || 0);
    const normalizedScanLimit = Number(scanLimit || deepScanLimit);
    const normalizedMatched = Number(matched || 0);
    const normalizedBatchSize = Number(batchSize || deepBatchScan);
    const batchEnd = Math.min(normalizedScanned + normalizedBatchSize, normalizedScanLimit);

    deepScanProgress.value = {
        scanned: batchEnd,
        scanLimit: normalizedScanLimit,
        matched: normalizedMatched,
    };
    deepScanSummary.value = `正在扫描 ${normalizedScanned + 1} - ${batchEnd} / ${normalizedScanLimit} 条 VIN，已命中 ${normalizedMatched} 条`;
}

function nextScannedTotalFromResponse(data, currentScannedTotal) {
    const responseScannedTotal = Number(data.scannedTotal);
    if (Number.isFinite(responseScannedTotal) && responseScannedTotal > currentScannedTotal) {
        return responseScannedTotal;
    }

    const responseBatchScanned = Number(data.batchScanned ?? data.scanned ?? 0);
    if (Number.isFinite(responseBatchScanned) && responseBatchScanned > 0) {
        return currentScannedTotal + responseBatchScanned;
    }

    return currentScannedTotal;
}

function normalizeModeQueryState() {
    const currentModeId = modeId.value.trim().toUpperCase();
    if (lastModeId.value && lastModeId.value !== currentModeId) {
        Object.keys(selectionValues).forEach((key) => delete selectionValues[key]);
        resetModeSelection();
        resetFilterRequirements();
    }
    lastModeId.value = currentModeId;
}

function buildModeQueryParams(options = {}) {
    const params = new URLSearchParams({
        modeId: modeId.value,
        year: selectedYear.value,
        limit: String(modeResultLimit),
        matchLevel: 'precise',
    });

    if (options.scanLimit) {
        params.set('scanLimit', String(options.scanLimit));
    }
    if (options.batchScan) {
        params.set('batchScan', String(options.batchScan));
    }
    if (options.cursor) {
        params.set('cursor', options.cursor);
    }
    if (Number.isFinite(options.scannedTotal) && options.scannedTotal > 0) {
        params.set('scannedTotal', String(options.scannedTotal));
    }

    Object.entries(selectionValues).forEach(([key, value]) => {
        if (value) {
            params.set(key, value);
        }
    });

    return params;
}

async function requestModeVin(options = {}) {
    const params = buildModeQueryParams(options);
    const response = await getSomeOne(serverName.value, `queryModeVin?${params.toString()}`);
    return response.data || {};
}

function mergeUniqueVinRows(target, rows, limit) {
    const seen = new Set(target.map((row) => row.vin).filter(Boolean));
    rows.forEach((row) => {
        if (target.length >= limit) {
            return;
        }
        if (!row || !row.vin || seen.has(row.vin)) {
            return;
        }
        seen.add(row.vin);
        target.push(row);
    });
}

async function handleSearch() {
    if (loading.value) {
        return;
    }

    if (queryType.value === 'mode') {
        await handleQueryModeVin();
    } else {
        await handleQueryDeliverVin();
    }
}

watch(queryType, () => {
    queried.value = false;
    tableData.value = [];
    modeQuerySuccess.value = false;
    resetDeepScanProgress();
    resetModeSelection();
    resetFilterRequirements();
    resetQueryMeta();
});

async function handleQueryDeliverVin() {
    if (loading.value) {
        return;
    }

    if (!validateDeliver()) {
        return;
    }

    loading.value = true;
    queried.value = true;
    resetDeepScanProgress();
    resetModeSelection();
    resetFilterRequirements();
    resetQueryMeta();

    try {
        const params = new URLSearchParams({
            deliver: deliverCode.value,
            year: selectedYear.value,
            limit: '10',
        });
        const response = await getSomeOne(serverName.value, `queryDeliverVin?${params.toString()}`);
        const data = response.data || {};

        if (data.success === false) {
            tableData.value = [];
            resetQueryMeta();
            ElMessage.error(data.message || 'Deliver 查询失败');
            return;
        }

        tableData.value = Array.isArray(data.data) ? data.data : [];
        updateQueryMeta(data);
        summary.value = {
            deliver: data.deliver || deliverCode.value,
            carname: data.carname || '',
            year: data.year || selectedYear.value,
            scanned: data.scanned || 0,
        };

        if (tableData.value.length) {
            ElMessage.success(`找到 ${tableData.value.length} 条 VIN`);
        } else {
            ElMessage.warning('没有找到匹配 VIN，可切换年款或服务器再试');
        }
    } catch (error) {
        tableData.value = [];
        resetQueryMeta();
        ElMessage.error('Deliver 查询失败');
    } finally {
        loading.value = false;
    }
}

async function handleQueryModeVin(options = {}) {
    if (loading.value) {
        return;
    }

    if (!validateModeId()) {
        return;
    }

    loading.value = true;
    queried.value = true;
    resetDeepScanProgress();
    resetFilterRequirements();
    resetQueryMeta();

    try {
        normalizeModeQueryState();
        const data = await requestModeVin(options);

        if (data.success === false) {
            tableData.value = [];
            modeQuerySuccess.value = false;
            updateModeSummary(data);
            resetQueryMeta();
            resetModeSelection(data);
            updateFilterRequirements(data);
            ElMessage.error(data.message || '功能模式号查询失败');
            return;
        }

        modeQuerySuccess.value = true;
        tableData.value = Array.isArray(data.data) ? data.data : [];
        updateModeSummary(data);
        updateQueryMeta(data);
        resetModeSelection(data);
        updateFilterRequirements(data);

        if (needUserSelection.value) {
            ElMessage.warning(modeSelectionNoticeText(data.selectionStage));
        } else if (tableData.value.length) {
            ElMessage.success(`找到 ${tableData.value.length} 条命中 VIN`);
        } else {
            const noResultText = options.deep
                ? '深度查询仍未找到命中 VIN，请主动切换服务器查询：国内的切海外，海外的切国内。'
                : (queryMeta.value.source === 'vdc_vin_deliver'
                    ? (queryMeta.value.hasMore
                        ? '当前索引页未命中，可点击深度查询继续索引分页'
                        : 'VIN 索引中未找到命中 VIN，可切换年款或服务器再试')
                    : modeNoResultTip);
            ElMessage.warning(noResultText);
        }
    } catch (error) {
        tableData.value = [];
        modeQuerySuccess.value = false;
        resetQueryMeta();
        ElMessage.error('功能模式号查询失败');
    } finally {
        loading.value = false;
    }
}

async function handleDeepQueryModeVin() {
    if (loading.value || !canDeepQuery.value) {
        return;
    }

    if (!validateModeId()) {
        return;
    }

    loading.value = true;
    queried.value = true;

    const accumulated = [];
    let cursor = '';
    let scannedTotal = 0;
    let scanLimit = Number(deepScanLimit);

    try {
        normalizeModeQueryState();
        modeQuerySuccess.value = true;
        updateDeepScanProgress(0, Number(deepScanLimit), 0);
        ElMessage.info(`深度查询按每批 ${deepBatchScan} 条扫描，最多扫描 ${deepScanLimit} 条 VIN`);

        while (accumulated.length < modeResultLimit && scannedTotal < scanLimit) {
            updateDeepScanBatchProgress(scannedTotal, scanLimit, accumulated.length, Number(deepBatchScan));
            const data = await requestModeVin({
                scanLimit: deepScanLimit,
                batchScan: deepBatchScan,
                cursor,
                scannedTotal,
            });

            if (data.success === false) {
                tableData.value = [];
                modeQuerySuccess.value = false;
                updateModeSummary(data);
                resetQueryMeta();
                resetModeSelection(data);
                updateFilterRequirements(data);
                resetDeepScanProgress();
                ElMessage.error(data.message || '深度查询失败');
                return;
            }

            modeQuerySuccess.value = true;
            updateModeSummary(data);
            updateQueryMeta(data);
            resetModeSelection(data);
            updateFilterRequirements(data);

            const rows = Array.isArray(data.data) ? data.data : [];
            mergeUniqueVinRows(accumulated, rows, modeResultLimit);
            tableData.value = [...accumulated];

            const previousScannedTotal = scannedTotal;
            scannedTotal = nextScannedTotalFromResponse(data, scannedTotal);
            const responseScanLimit = Number(data.scanLimit || deepScanLimit);
            scanLimit = data.scanExhausted
                ? Number(data.effectiveScanLimit || scannedTotal)
                : responseScanLimit;
            updateDeepScanProgress(scannedTotal, scanLimit, accumulated.length);

            if (scannedTotal <= previousScannedTotal && data.hasMore) {
                ElMessage.warning('深度查询进度未推进，请稍后重试或切换服务器');
                break;
            }

            if (needUserSelection.value || accumulated.length >= modeResultLimit) {
                break;
            }

            cursor = data.nextCursor || '';
            if (!data.hasMore || !cursor) {
                break;
            }
        }

        if (needUserSelection.value) {
            const stage = selectionQuestions.value[0]?.key === 'deliver'
                ? 'deliver'
                : (hasInfoSelectionQuestions.value && !hasSelectableSelectionQuestions.value ? 'feature_info' : 'feature');
            ElMessage.warning(modeSelectionNoticeText(stage));
        } else if (tableData.value.length) {
            ElMessage.success(`深度查询找到 ${tableData.value.length} 条命中 VIN，已扫描 ${scannedTotal} 条`);
        } else {
            ElMessage.warning('深度查询仍未找到命中 VIN，请主动切换服务器查询：国内的切海外，海外的切国内。');
        }
    } catch (error) {
        tableData.value = [];
        modeQuerySuccess.value = false;
        resetDeepScanProgress();
        resetQueryMeta();
        ElMessage.error('深度查询失败');
    } finally {
        loading.value = false;
    }
}

function matchStatusType(status) {
    const map = {
        matched: 'success',
        need_selection: 'warning',
        uncertain: 'info',
        not_matched: 'danger',
    };
    return map[status] || 'info';
}

function matchStatusText(status) {
    const map = {
        matched: '已匹配',
        need_selection: '需满足条件',
        uncertain: '不确定',
        not_matched: '不匹配',
    };
    return map[status] || status || '-';
}

async function handleDownloadCommand(row) {
    const protocols = Array.isArray(row.protocols) ? row.protocols.filter(Boolean) : [];
    if (!protocols.length) {
        ElMessage.warning('该 VIN 没有可用协议，无法生成命令文本');
        return;
    }

    try {
        const useBaseURL = getUseBaseURL();
        const deliver = row.deliver || deliverCode.value;

        for (const protocol of protocols) {
            const url = `downloadVinCmd?vin=${encodeURIComponent(row.vin)}&prtl=${encodeURIComponent(protocol)}&deliver=${encodeURIComponent(deliver)}`;
            await DownloadSomeOne(useBaseURL, url);
        }

        ElMessage.success('命令文本下载请求已发送');
    } catch (error) {
        ElMessage.error('命令文本下载失败');
    }
}
</script>

<style scoped>
.deliver-page {
    min-height: 100%;
    background: #f5f8fb;
}

.data-container {
    padding: 20px;
    overflow-y: auto;
}

.query-panel,
.mode-summary,
.selection-panel,
.requirements-panel,
.deep-progress-panel,
.table-section {
    background: #ffffff;
    border: 1px solid #e6eef7;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(31, 60, 92, 0.06);
}

.query-panel {
    padding: 18px;
    margin-bottom: 16px;
}

.query-title {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.title-mark {
    width: 10px;
    height: 38px;
    border-radius: 8px;
    background: linear-gradient(180deg, #3b82f6, #14b8a6);
}

.query-title h3,
.section-header h3 {
    margin: 0;
    color: #102033;
    font-size: 18px;
    font-weight: 700;
}

.query-title p,
.section-header p {
    margin: 4px 0 0;
    color: #6b7c93;
    font-size: 13px;
}

.query-controls {
    display: grid;
    grid-template-columns: 150px 190px minmax(240px, 1fr) 140px auto;
    gap: 12px;
    align-items: center;
}

.rule-note {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    padding: 10px 12px;
    color: #416174;
    font-size: 13px;
    line-height: 1.5;
    background: #f2f8ff;
    border: 1px solid #d8e9fb;
    border-radius: 8px;
}

.rule-note .el-icon {
    flex: 0 0 auto;
    color: #409eff;
}

.server-select,
.year-select,
.main-input,
.query-type {
    width: 100%;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.summary-card {
    min-height: 78px;
    padding: 14px 16px;
    background: linear-gradient(180deg, #ffffff, #f8fbff);
    border: 1px solid #e3edf8;
    border-radius: 8px;
}

.summary-card span {
    display: block;
    color: #6b7c93;
    font-size: 13px;
    margin-bottom: 8px;
}

.summary-card strong {
    color: #102033;
    font-size: 19px;
    font-weight: 700;
    word-break: break-word;
}

.mode-summary,
.selection-panel,
.requirements-panel {
    padding: 14px 16px;
    margin-bottom: 16px;
}

.selected-deliver {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 24px;
}

.selection-header {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #915a00;
    font-weight: 700;
    margin-bottom: 12px;
}

.selection-controls {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 18px;
}

.selection-controls :deep(.el-form-item) {
    margin-bottom: 0;
}

.selection-controls :deep(.el-select) {
    width: 100%;
}

.condition-info-group {
    grid-column: 1 / -1;
    min-width: 0;
}

.condition-info-title {
    color: #41546b;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 8px;
}

.condition-tags {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.condition-tags :deep(.el-tag) {
    max-width: 100%;
    height: auto;
    padding: 5px 8px;
    white-space: normal;
    line-height: 1.35;
}

.requirements-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.requirements-header h3 {
    margin: 0;
    color: #102033;
    font-size: 16px;
    font-weight: 700;
}

.requirements-header p {
    margin: 4px 0 0;
    color: #6b7c93;
    font-size: 13px;
    line-height: 1.45;
}

.requirements-content {
    display: grid;
    gap: 14px;
}

.requirement-group {
    min-width: 0;
}

.requirement-title {
    color: #41546b;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 8px;
}

.requirements-table {
    width: 100%;
}

.requirements-table :deep(.el-table__header th) {
    background: #f7f9fc !important;
    color: #41546b;
    font-weight: 700;
}

.assembly-tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.assembly-tags :deep(.el-tag) {
    max-width: 100%;
    height: auto;
    white-space: normal;
    line-height: 1.35;
}

.deep-progress-panel {
    padding: 14px 16px;
    margin-bottom: 16px;
}

.deep-progress-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.deep-progress-header span {
    color: #6b7c93;
    font-size: 13px;
}

.deep-progress-header strong {
    color: #102033;
    font-size: 14px;
    font-weight: 700;
    text-align: right;
}

.table-section {
    position: relative;
    min-height: 360px;
    padding: 18px;
}

.table-loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    background: rgba(255, 255, 255, 0.88);
    border-radius: 8px;
}

.loading-spinner {
    width: 34px;
    height: 34px;
    border: 3px solid #d8e9fb;
    border-top-color: #409eff;
    border-radius: 50%;
    animation: deliver-loading-spin 0.9s linear infinite;
}

.loading-text {
    max-width: min(92%, 720px);
    color: #1677ff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.45;
    text-align: center;
    word-break: break-word;
}

@keyframes deliver-loading-spin {
    to {
        transform: rotate(360deg);
    }
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.section-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
}

.custom-table {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
}

.custom-table :deep(.el-table__header th) {
    background: #f4f8fd !important;
    color: #41546b;
    font-weight: 700;
}

.protocol-tags,
.action-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-wrap: wrap;
}

.mdx-tags {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    flex-wrap: wrap;
}

.action-buttons :deep(.el-button) {
    margin-left: 0;
}

.muted-text {
    color: #9aa8b8;
}

@media screen and (max-width: 1100px) {
    .query-controls {
        grid-template-columns: 1fr 1fr;
    }
}

@media screen and (max-width: 900px) {
    .summary-grid,
    .selection-controls {
        grid-template-columns: 1fr 1fr;
    }
}

@media screen and (max-width: 640px) {
    .data-container {
        padding: 12px;
    }

    .query-controls,
    .summary-grid,
    .selection-controls {
        grid-template-columns: 1fr;
    }

    .section-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .deep-progress-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .deep-progress-header strong {
        text-align: left;
    }

    .section-actions {
        justify-content: flex-start;
        width: 100%;
    }
}

/* ── Dark-mode tonal buttons ── */

:global(html.dark .deliver-page .el-button--primary) {
    color: var(--wf-status-info-text) !important;
    border-color: rgba(96, 165, 250, 0.46) !important;
    background: var(--wf-status-info-bg) !important;
    box-shadow: none !important;
}

:global(html.dark .deliver-page .el-button--primary:not(.is-disabled):hover),
:global(html.dark .deliver-page .el-button--primary:not(.is-disabled):focus) {
    color: #dbeafe !important;
    border-color: rgba(96, 165, 250, 0.7) !important;
    background: rgba(59, 130, 246, 0.24) !important;
}

:global(html.dark .deliver-page .el-button--warning) {
    color: var(--wf-status-warning-text) !important;
    border-color: rgba(252, 211, 77, 0.42) !important;
    background: var(--wf-status-warning-bg) !important;
    box-shadow: none !important;
}

:global(html.dark .deliver-page .el-button--warning:not(.is-disabled):hover),
:global(html.dark .deliver-page .el-button--warning:not(.is-disabled):focus) {
    color: #fde68a !important;
    border-color: rgba(252, 211, 77, 0.68) !important;
    background: rgba(245, 158, 11, 0.24) !important;
}

:global(html.dark .deliver-page .el-button:not(.el-button--primary):not(.el-button--warning):not(.el-button--danger)) {
    color: var(--wf-text-regular) !important;
    border-color: var(--wf-control-border) !important;
    background: var(--wf-surface-soft) !important;
    box-shadow: none !important;
}

:global(html.dark .deliver-page .el-button:not(.el-button--primary):not(.el-button--warning):not(.el-button--danger):not(.is-disabled):hover),
:global(html.dark .deliver-page .el-button:not(.el-button--primary):not(.el-button--warning):not(.el-button--danger):not(.is-disabled):focus) {
    color: var(--wf-text-primary) !important;
    border-color: #64748b !important;
    background: var(--wf-hover-bg) !important;
}

:global(html.dark .deliver-page .el-button.is-disabled) {
    opacity: 0.58;
    box-shadow: none !important;
}
</style>
